<template>
  <div
    v-if="$can('set-income-costs', 'YachtModel')"
    style="height: 100%"
  >
    <v-container
      class="py-0"
      style="height: 100%"
    >
      <v-row
        no-gutters
        class="mb-4"
        align="center"
      >
        <v-col cols="auto">
          <div class="headline">
            Yacht Cost Calculator
          </div>
        </v-col>
      </v-row>

      <v-tabs
        v-model="countryId"
        background-color="transparent"
      >
        <v-tab
          v-for="country in countries"
          :key="country.id"
          :href="'#'+country.id"
          class="text-none"
        >
          <flag
            :iso="country.code"
            :squared="false"
            class="mr-2"
          />
          {{ country.name }}
        </v-tab>
      </v-tabs>

      <v-row
        no-gutters
        class="mb-4 mt-4"
        align="center"
      >
        <v-col
          cols="12"
          md="4"
        >
          <v-select
            v-model="baseId"
            :items="basesForSelectedCountry"
            item-text="name"
            item-value="id"
            label="Base (optional)"
            clearable
            outlined
            dense
            hide-details
          />
        </v-col>
      </v-row>

      <v-card class="pa-0 mb-3">
        <div
          v-if="yachtTypesDirty.length === 0"
          class="pa-6 text-center"
        >
          <v-progress-circular
            :size="50"
            :width="3"
            indeterminate
          />
        </div>
        <v-simple-table
          v-else
        >
          <thead>
            <tr>
              <th />
              <th
                v-for="costElement in costData"
                :key="costElement.key + 'row'"
                style="width: 165px"
              >
                {{ costElement.label }}
              </th>
            </tr>
          </thead>
          <tbody v-if="selectedCountryId && yachtTypesDirty[selectedCountryId]">
            <tr
              v-for="yachtType in yachtTypesDirty[selectedCountryId]"
              :key="selectedCountryId + yachtType.id + 'header'"
            >
              <td>
                {{ yachtType.label }}
              </td>
              <td
                v-for="costElement in yachtType.cost_data"
                :key="costElement.cost_element_key + yachtType.id"
                class="pa-0"
                style="width: 165px"
              >
                <cost-control-table-column
                  :key="'tablecolumn'+costElement.cost_element_key + yachtType.id"
                  v-model="costElement.value"
                  :calculation-type.sync="costElement.calculation_type"
                  :currency="selectedCountryCurrency"
                />
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card>
      <v-row class="mb-16">
        <v-spacer />
        <v-col cols="auto">
          <v-btn
            color="primary"
            :loading="submitting"
            large
            @click="save()"
          >
            Save Changes
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>

import YachtType from '@/models/YachtType'
import { cloneDeep } from 'lodash'
import CostControlTableColumn from '@/components/settings/costControl/CostControlTableColumn.vue'

export default {
    name: 'CostControlIndex',
    components: {
      CostControlTableColumn
    },
    data() {
      return {
        countryId: null,
        baseId: null,
        yachtTypesDirty: [],
        submitting: false
      }
    },
    computed: {
      countries() {
        return this.$store.state.settings.countries.data.filter(c => c.chartering)
      },
      yachtManufacturers() {
        return this.$store.state.settings.yachtManufacturers.data
      },
      yachtTypes() {
        return this.$store.state.settings.yachtTypes.data
      },
      selectedCountryId() {
        if (!this.countryId) return undefined

        return parseInt(this.countryId)
      },
      selectedCountryCurrency() {
        if (!this.selectedCountryId) return 'EUR'

        const country = this.countries.find(c => c.id === this.selectedCountryId)

        return country.currency
      },
      basesForSelectedCountry() {
        if (!this.selectedCountryId) return []

        return this.$store.state.business.bases.data.filter(b => b.country_id === this.selectedCountryId)
      }
    },
    watch: {
      yachtTypes() {
        this.loadYachtTypes()
      },
      countries() {
        this.countryId = this.countries[0].id.toString()

        this.loadYachtTypes()
      },
      countryId() {
        this.baseId = null
      },
      baseId() {
        this.loadYachtTypes()
      }
    },
    created() {
      if (this.countries.length > 0) this.countryId = this.countries[0].id.toString()

      this.loadYachtTypes()

      this.costData = YachtType.COST_DATA
      this.calculationTypeData = YachtType.CALCULATION_TYPE_DATA
    },
    methods: {
      loadYachtTypes() {
        const countries = {}

        for (let country of this.countries) {
          countries[country.id] = cloneDeep(this.yachtTypes).map(type => {
            const countryCostDataRow = type.country_costs.find(cc => {
              const matchesCountry = cc.country_id === country.id
              const matchesBase = this.baseId ? cc.base_id === this.baseId : (cc.base_id === null || cc.base_id === undefined)
              return matchesCountry && matchesBase
            })

            if (!countryCostDataRow) {
              type.cost_data = {}

              Object.values(YachtType.COST_DATA).map(costDataModelItem => {
                let costDataItem = cloneDeep(YachtType.costModel)

                costDataItem.cost_element_key = costDataModelItem.key
                costDataItem.calculation_type = YachtType.CALCULATION_TYPE_FIXED
                costDataItem.value = 0

                type.cost_data[costDataModelItem.key] = costDataItem
              })
            } else {
              type.cost_data = countryCostDataRow.cost_data
            }

            return type
          })
        }

        this.yachtTypesDirty = countries
      },
      async save() {
        try {
          this.submitting = true

          const data = {
            country_id: this.selectedCountryId,
            base_id: this.baseId,
            yacht_types: this.yachtTypesDirty[this.selectedCountryId].map(yt => {
              return {
                id: yt.id,
                cost_data: yt.cost_data
              }
            })
          }

          await YachtType.api.updateCosts(data)

          this.$store.dispatch('alert', {
            text: 'Cost calculation successfully updated',
            color: 'success'
          })
        } catch (e) {
          this.$store.dispatch('alert', {
            text: 'Something went wrong',
            color: 'error'
          })
          throw e
        } finally {
          this.submitting = false
        }
      }
    }
  }
</script>

<style lang="css" scoped>
  td, th {
    border-right: 1px solid rgba(0, 0, 0, 0.12);
  }
  td:last-child, th:last-child {
    border-right: none;
  }
</style>
